import { Close as CloseIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import CollapsibleSection from '../../../../../components/common/CollapsibleSection';
import { prebuiltCoHostProfiles } from '../../../../../data/prebuiltProfiles';
import {
  CoHostPersonality,
  StudioConfig
} from '../../../../../types/studio.types';
// Removed direct CastingDirectorOptions usage in favor of new VoiceConfigurationSection
import AIAssistantIcon from '../../common/AIAssistantIcon';
// Import only the type for the prop
import { AudioPreviewResponse } from '../../common/AudioPreview';
import TabPanel from '../../TabPanel';
import VoiceConfigurationSection from './VoiceConfigurationSection';

interface HostConfigSectionProps {
  studioConfig: StudioConfig;
  websitePK: string;
  errors: Record<string, string>;
  isLoading: boolean;
  isAiGeneratingHostPersonalities: boolean;
  hostElapsedTime: number;
  hostGenerationComplete: boolean;
  onHostAIButtonClick: () => void;
  onHostGenerationCompleteClose: () => void;
  onStudioConfigChange: (config: StudioConfig) => void;
  onPlayVoiceSample: (payload: CoHostPersonality | { host: CoHostPersonality; voiceText?: string }) => Promise<AudioPreviewResponse>;
  onHandleFormatChange: (format: 'cohost' | 'solo') => void;
  icon?: React.ReactNode;
  status?: {
    enabled: boolean;
    state: 'valid' | 'warning' | 'error';
    missingFields?: string[];
  };
}

const HostConfigSection: React.FC<HostConfigSectionProps> = ({
  studioConfig,
  websitePK,
  errors,
  isLoading,
  isAiGeneratingHostPersonalities,
  hostElapsedTime,
  hostGenerationComplete,
  onHostAIButtonClick,
  onHostGenerationCompleteClose,
  onStudioConfigChange,
  onPlayVoiceSample,
  onHandleFormatChange,
  icon,
  status,
}) => {
  // State for co-host tab selection
  const [coHostTabValue, setCoHostTabValue] = useState(0);

  // State for pre-built profile selections
  const [selectedCoHostProfileIds, setSelectedCoHostProfileIds] = useState<string[]>(
    studioConfig.coHosts.map(() => '')
  );

  // Format time as MM:SS for displaying elapsed time
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  const handleFormatChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFormat = event.target.value as 'cohost' | 'solo';
    onHandleFormatChange(newFormat);
    // Removed direct call to onStudioConfigChange here to avoid overwriting parent logic
  }

  // Handle host identification change
  const handleHostIdentificationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const identification = event.target.value as 'named' | 'anonymous';
    onStudioConfigChange({
      ...studioConfig,
      hostIdentification: identification,
    });
  }

  // Handle co-host tab change
  const handleCoHostTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCoHostTabValue(newValue);
  };

  // Handle co-host field changes
  const handleCoHostChange = (index: number, field: keyof CoHostPersonality, value: string) => {
    const updatedCoHosts = [...studioConfig.coHosts];
    updatedCoHosts[index] = {
      ...updatedCoHosts[index],
      [field]: value,
    };

    onStudioConfigChange({
      ...studioConfig,
      coHosts: updatedCoHosts,
    });
  };

  // ...existing code (removed handleVoiceTypeChange, now handled inside VoiceConfigurationSection)...

  // Handle text input changes for relationship dynamics
  const handleTextChange = (field: string, value: string) => {
    onStudioConfigChange({
      ...studioConfig,
      [field]: value,
    });
  };

  // Handle selecting a pre-built co-host profile
  const handleSelectPrebuiltCoHost = (event: SelectChangeEvent<string>, index: number) => {
    const profileId = event.target.value;
    const newSelectedIds = [...selectedCoHostProfileIds];
    newSelectedIds[index] = profileId;
    setSelectedCoHostProfileIds(newSelectedIds);

    if (!profileId) return;

    const selectedProfile = prebuiltCoHostProfiles.find(p => p.id === profileId);
    if (!selectedProfile || !selectedProfile.coHostProfile) return;

    const updatedCoHosts = [...studioConfig.coHosts];
    updatedCoHosts[index] = {
      ...selectedProfile.coHostProfile,
    };

    onStudioConfigChange({
      ...studioConfig,
      coHosts: updatedCoHosts,
    });
  };

  // Helper function for tab a11y props
  const a11yProps = (index: number) => {
    return {
      id: `cohost-tab-${index}`,
      'aria-controls': `cohost-tabpanel-${index}`,
    };
  };

  // AI generation button for the section header
  const aiGenerationButton = (
    <Tooltip title="Generate host personalities with AI">
      <span> {/* Wrap with span to allow tooltip on disabled button */}
        <IconButton
          color="primary"
          onClick={onHostAIButtonClick}
          disabled={isLoading || isAiGeneratingHostPersonalities}
          sx={{
            ml: 1,
            '&:hover': {
              backgroundColor: 'action.hover',
            }
          }}
          aria-label="Generate with AI"
        >
          {isAiGeneratingHostPersonalities ? (
            <CircularProgress size={24} color="primary" />
          ) : (
            <AIAssistantIcon />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <CollapsibleSection 
      title="Host Format"
      icon={icon}
      actionButton={aiGenerationButton}
      status={status}
    >
      <FormControl component="fieldset">
        <RadioGroup
          name="host-format"
          value={studioConfig.format}
          onChange={handleFormatChange}
          sx={{ mb: 1 }}
        >
          <FormControlLabel
            value="solo"
            control={<Radio disabled={isLoading} />}
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Solo Host
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  A single host will present the news in a more traditional style
                </Typography>
              </Box>
            }
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            value="cohost"
            control={<Radio disabled={isLoading} />}
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Co-Host Format
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Two hosts will discuss the news in a conversational style
                </Typography>
              </Box>
            }
          />
        </RadioGroup>
      </FormControl>

      {/* NEW: Reading Style selection */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        Reading Style
      </Typography>
      <FormControl component="fieldset">
        <RadioGroup
          name="reading-style"
          value={studioConfig.readingStyle || 'podcast'}
          onChange={(e) => onStudioConfigChange({
            ...studioConfig,
            readingStyle: e.target.value as 'podcast' | 'verbatim'
          })}
          sx={{ mb: 3 }}
        >
          <FormControlLabel
            value="podcast"
            control={<Radio disabled={isLoading} />}
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Podcast style conversational audio
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Hosts paraphrase & discuss article content naturally rather than reading every word
                </Typography>
              </Box>
            }
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            value="verbatim"
            control={<Radio disabled={isLoading} />}
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Read the article verbatim
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Host(s) will read the original article text word-for-word
                </Typography>
              </Box>
            }
          />
        </RadioGroup>
      </FormControl>

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        Host Identification
      </Typography>
      <FormControl component="fieldset">
        <RadioGroup
          name="host-identification"
          value={studioConfig.hostIdentification || 'named'}
          onChange={handleHostIdentificationChange}
          sx={{ mb: 3 }}
        >
          <FormControlLabel
            value="named"
            control={<Radio disabled={isLoading} />}
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Named Personalities
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Hosts will introduce themselves by name and refer to each other by name during the show
                </Typography>
              </Box>
            }
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            value="anonymous"
            control={<Radio disabled={isLoading} />}
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Anonymous Hosts
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Hosts will not mention their names or introduce themselves during the show
                </Typography>
              </Box>
            }
          />
        </RadioGroup>
      </FormControl>

      <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 3, mt: 3 }}>
        Define the {studioConfig.format === 'cohost' ? 'personalities of your co-hosts' : 'personality of your host'}.
        {studioConfig.hostIdentification === 'anonymous' ? 
          ' With anonymous mode, hosts will not introduce themselves or mention names, but their voice and style will still reflect their personality.' : 
          ' Named personalities will introduce themselves and refer to each other by name during the show.'}
        Profiles are used to generate natural-sounding
        {studioConfig.format === 'cohost' ? ' dialogue that matches each host\'s unique style and background.' : ' narration that matches the host\'s unique style and background.'}
      </Typography>

      {isAiGeneratingHostPersonalities && (
        <Alert severity="info" sx={{ mb: 2 }}>
          AI is generating host personalities based on your audio style guide and demographics. This will take a few minutes...
          <Typography variant="body2" sx={{ mt: 1 }}>
            Time elapsed: {formatTime(hostElapsedTime)}
          </Typography>
        </Alert>
      )}

      {hostGenerationComplete && !isAiGeneratingHostPersonalities && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Host personalities generation completed in {formatTime(hostElapsedTime)}!
          <IconButton
            size="small"
            onClick={onHostGenerationCompleteClose}
            sx={{ ml: 1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Alert>
      )}

      <Typography variant="h6" gutterBottom>
        {studioConfig.format === 'cohost' ? 'Co-Host Personalities' : 'Host Personality'}
      </Typography>

      {studioConfig.format === 'cohost' && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Host/Co-host Relationship Dynamics
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={5}
            label="Relationship Dynamics"
            value={studioConfig.relationshipDynamics || ''}
            onChange={(e) => handleTextChange('relationshipDynamics', e.target.value)}
            disabled={isLoading}
            placeholder="Describe how the hosts interact with each other, their conversational patterns, how they portray their relationship on air, how they handle disagreements, and the balance of speaking time between them."
            helperText="Define the dynamic between hosts to create natural, complementary interactions"
          />
        </Box>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={coHostTabValue}
          onChange={handleCoHostTabChange}
          aria-label="co-host tabs"
          variant="fullWidth"
        >
          {studioConfig.format === 'cohost' ? (
            // Show both tabs for co-host format
            studioConfig.coHosts.map((host, index) => (
              <Tab
                key={index}
                label={host.name || `Co-Host ${index + 1}`}
                {...a11yProps(index)}
              />
            ))
          ) : (
            // Show only one tab for solo format
            <Tab
              label={studioConfig.coHosts[0]?.name || 'Host'}
              {...a11yProps(0)}
            />
          )}
        </Tabs>
      </Box>

      {studioConfig.coHosts.map((host, index) => (
        <TabPanel key={index} value={coHostTabValue} index={index}>
          <Grid container spacing={2}>
            {/* Pre-built co-host profile selection */}
            <Grid item xs={12}>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id={`co-host-profile-select-label-${index}`}>Select Pre-built Co-Host</InputLabel>
                <Select
                  labelId={`co-host-profile-select-label-${index}`}
                  id={`co-host-profile-select-${index}`}
                  value={selectedCoHostProfileIds[index] || ''}
                  label="Select Pre-built Co-Host"
                  onChange={(e) => handleSelectPrebuiltCoHost(e, index)}
                  disabled={isLoading}
                >
                  <MenuItem value="">
                    <em>Select a profile or customize below</em>
                  </MenuItem>
                  {prebuiltCoHostProfiles.map((profile) => (
                    <MenuItem key={profile.id} value={profile.id}>
                      {profile.name} - {profile.description}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Choose a pre-built co-host as a starting point, then customize it below
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                Basic Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  border: !host.name || host.name.trim().length === 0 
                    ? '2px solid' 
                    : 'none',
                  borderColor: 'error.main',
                  borderRadius: 1,
                  p: !host.name || host.name.trim().length === 0 ? 1 : 0,
                }}
              >
                <TextField
                  fullWidth
                  required
                  label="Full Name"
                  value={host.name}
                  onChange={(e) => handleCoHostChange(index, 'name', e.target.value)}
                  error={!!errors[`studioConfig.coHosts[${index}].name`] || !host.name || host.name.trim().length === 0}
                  helperText={errors[`studioConfig.coHosts[${index}].name`] || (!host.name || host.name.trim().length === 0 ? 'Required field' : '')}
                  disabled={isLoading}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  border: !host.nickname || host.nickname.trim().length === 0 
                    ? '2px solid' 
                    : 'none',
                  borderColor: 'error.main',
                  borderRadius: 1,
                  p: !host.nickname || host.nickname.trim().length === 0 ? 1 : 0,
                }}
              >
                <TextField
                  fullWidth
                  required
                  label="Nickname"
                  value={host.nickname}
                  onChange={(e) => handleCoHostChange(index, 'nickname', e.target.value)}
                  error={!host.nickname || host.nickname.trim().length === 0}
                  helperText={!host.nickname || host.nickname.trim().length === 0 ? 'Required field' : 'How they\'re referred to on air'}
                  disabled={isLoading}
                  placeholder="How they're referred to on air"
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  border: !host.gender || host.gender.trim().length === 0 
                    ? '2px solid' 
                    : 'none',
                  borderColor: 'error.main',
                  borderRadius: 1,
                  p: !host.gender || host.gender.trim().length === 0 ? 1 : 0,
                }}
              >
                <TextField
                  fullWidth
                  required
                  label="Gender"
                  value={host.gender}
                  onChange={(e) => handleCoHostChange(index, 'gender', e.target.value)}
                  error={!host.gender || host.gender.trim().length === 0}
                  helperText={!host.gender || host.gender.trim().length === 0 ? 'Required field' : ''}
                  disabled={isLoading}
                  placeholder="male, female, non-binary, etc."
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Pronouns"
                value={host.pronouns}
                onChange={(e) => handleCoHostChange(index, 'pronouns', e.target.value)}
                disabled={isLoading}
                placeholder="he/him, she/her, they/them, etc."
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  border: !host.age || host.age.trim().length === 0 
                    ? '2px solid' 
                    : 'none',
                  borderColor: 'error.main',
                  borderRadius: 1,
                  p: !host.age || host.age.trim().length === 0 ? 1 : 0,
                }}
              >
                <TextField
                  fullWidth
                  required
                  label="Age"
                  value={host.age}
                  onChange={(e) => handleCoHostChange(index, 'age', e.target.value)}
                  error={!host.age || host.age.trim().length === 0}
                  helperText={!host.age || host.age.trim().length === 0 ? 'Required field' : ''}
                  disabled={isLoading}
                  placeholder="Age or age range"
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Personality Details
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Background & History"
                value={host.background}
                onChange={(e) => handleCoHostChange(index, 'background', e.target.value)}
                disabled={isLoading}
                placeholder="Describe the co-host's background, experience, and relevant history"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  border: !host.personalityTraits || host.personalityTraits.trim().length === 0 
                    ? '2px solid' 
                    : 'none',
                  borderColor: 'error.main',
                  borderRadius: 1,
                  p: !host.personalityTraits || host.personalityTraits.trim().length === 0 ? 1 : 0,
                }}
              >
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={4}
                  label="Personality Traits"
                  value={host.personalityTraits}
                  onChange={(e) => handleCoHostChange(index, 'personalityTraits', e.target.value)}
                  error={!host.personalityTraits || host.personalityTraits.trim().length === 0}
                  helperText={!host.personalityTraits || host.personalityTraits.trim().length === 0 ? 'Required field' : ''}
                  disabled={isLoading}
                  placeholder="Describe key personality traits (e.g., 'Calm & Collected', 'Witty & Dry', etc.)"
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  border: !host.speakingStyle || host.speakingStyle.trim().length === 0 
                    ? '2px solid' 
                    : 'none',
                  borderColor: 'error.main',
                  borderRadius: 1,
                  p: !host.speakingStyle || host.speakingStyle.trim().length === 0 ? 1 : 0,
                }}
              >
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={4}
                  label="Speaking Style"
                  value={host.speakingStyle}
                  onChange={(e) => handleCoHostChange(index, 'speakingStyle', e.target.value)}
                  error={!host.speakingStyle || host.speakingStyle.trim().length === 0}
                  helperText={!host.speakingStyle || host.speakingStyle.trim().length === 0 ? 'Required field' : ''}
                  disabled={isLoading}
                  placeholder="Describe speech patterns, cadence, and verbal quirks"
                />
              </Box>
            </Grid>

            {/* Voice Configuration moved to its own component */}
            <VoiceConfigurationSection
              host={host}
              index={index}
              studioConfig={studioConfig}
              websitePK={websitePK}
              isLoading={isLoading}
              onStudioConfigChange={onStudioConfigChange}
              onPlayVoiceSample={onPlayVoiceSample}
            />
          </Grid>
        </TabPanel>
      ))}
    </CollapsibleSection>
  );
};

export default HostConfigSection;